import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { 
  Users, LayoutDashboard, Building2,
  FileSpreadsheet, HelpCircle, Stethoscope, LogOut
} from "lucide-react";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { Button } from "@/components/ui/button";
import { LogoutConfirmDialog } from "@/components/ConfirmDialogs";
import LicensePurchaseModal from "@/components/LicensePurchaseModal";

const PartnerLayout = () => {
  const { user, token, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  useEffect(() => {
    const handler = () => setShowLicenseModal(true);
    window.addEventListener('openLicensePurchase', handler);
    return () => window.removeEventListener('openLicensePurchase', handler);
  }, []);

  useEffect(() => {
    // Redirect if not partner_admin or admin
    if (user && user.role !== "partner_admin" && user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchPartnerInfo = async () => {
      try {
        const response = await fetch(`${API}/partner-admin/overview`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          setPartnerInfo(data.partner);
        }
      } catch (error) {
        console.error("Failed to fetch partner info:", error);
      }
    };
    
    if (token) fetchPartnerInfo();
  }, [token]);

  const tabs = [
    { path: "/partner", label: t('partner.overview'), icon: LayoutDashboard, exact: true },
    { path: "/partner/patients", label: t('physician.my_patients'), icon: Users },
    { path: "/partner/physicians", label: t('partner.physicians'), icon: Stethoscope },
    { path: "/partner/reports", label: "Reports", icon: FileSpreadsheet },
    { path: "/partner/support", label: "Support", icon: HelpCircle },
  ];

  const isActive = (tab) => {
    if (tab.exact) {
      return location.pathname === tab.path;
    }
    return location.pathname.startsWith(tab.path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              <span className="font-bold text-secondary text-sm sm:text-base">{t('partner.dashboard')}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageSwitcher variant="compact" />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-secondary">
                  {partnerInfo?.organization_name || user?.name}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {partnerInfo?.partner_type || t('admin.partner')}
                </p>
              </div>
              <PlateMindLogo className="w-8 h-8 sm:w-10 sm:h-10" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLogoutDialog(true)}
                className="text-muted-foreground hover:text-red-500"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <nav className="flex gap-0.5 sm:gap-1 overflow-x-auto" style={{WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive(tab)
                      ? "border-green-600 text-green-600"
                      : "border-transparent text-muted-foreground hover:text-secondary"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  <span className={`${isActive(tab) ? "" : "hidden sm:inline"}`}>{tab.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <Outlet />
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-orange-100 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Need help? Contact <a href="mailto:support@platemind.app" className="text-orange-500 hover:underline">support@platemind.app</a>
            </p>
            <p className="text-xs text-gray-400 text-center">
              PlateMind provides nutrition guidance and does not replace professional medical advice.
            </p>
          </div>
        </div>
      </footer>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={() => {
          logout();
          navigate("/login?type=professional");
        }}
      />
      
      <LicensePurchaseModal open={showLicenseModal} onOpenChange={setShowLicenseModal} />
    </div>
  );
};

export default PartnerLayout;
